import { Suspense, lazy, useEffect } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { UrgSaludAssistant } from "@/components/assistant/UrgSaludAssistant";
import { ThemeProvider } from "@/components/ThemeProvider";
import { PushNotificationInit } from "@/components/PushNotificationInit";

// Layouts
import PatientLayout from "./components/layout/PatientLayout";
import ProfessionalLayout from "./components/layout/ProfessionalLayout";
import AdminLayout from "./components/layout/AdminLayout";

// Public pages
import Index from "./pages/Index";
const Auth = lazy(() => import("./pages/Auth"));
const OAuthCallback = lazy(() => import("./pages/OAuthCallback"));
const Urgencia = lazy(() => import("./pages/Urgencia"));
const NotFound = lazy(() => import("./pages/NotFound"));
const TerminosCondiciones = lazy(() => import("./pages/TerminosCondiciones"));
const PoliticaPrivacidad = lazy(() => import("./pages/PoliticaPrivacidad"));

// Dashboards - lazy loaded
const PatientDashboard = lazy(() => import("./pages/dashboards/PatientDashboard"));
const ProfessionalDashboard = lazy(() => import("./pages/dashboards/ProfessionalDashboard"));
const AdminDashboard = lazy(() => import("./pages/dashboards/AdminDashboard"));

// Patient pages - lazy loaded
const BuscarProfesionales = lazy(() => import("./pages/patient/BuscarProfesionales"));
const MisCitas = lazy(() => import("./pages/patient/MisCitas"));
const HistorialMedico = lazy(() => import("./pages/patient/HistorialMedico"));
const Favoritos = lazy(() => import("./pages/patient/Favoritos"));

const ReferidosUsuario = lazy(() => import("./pages/patient/ReferidosUsuario"));
const FaqUsuario = lazy(() => import("./pages/patient/FaqUsuario"));
const SolicitarServicio = lazy(() => import("./pages/patient/SolicitarServicio"));
const SolicitarAtencion = lazy(() => import("./pages/patient/SolicitarAtencion"));
const ResultadosBusqueda = lazy(() => import("./pages/patient/ResultadosBusqueda"));
const ComunidadUsuario = lazy(() => import("./pages/patient/ComunidadUsuario"));
const ProfesionalesArea = lazy(() => import("./pages/patient/ProfesionalesArea"));
const PerfilPublicoProfesional = lazy(() => import("./pages/patient/PerfilPublicoProfesional"));
const AgendarCita = lazy(() => import("./pages/patient/AgendarCita"));
const MiCuenta = lazy(() => import("./pages/patient/MiCuenta"));

// Professional pages - lazy loaded
const PerfilProfesional = lazy(() => import("./pages/professional/PerfilProfesional"));
const Disponibilidad = lazy(() => import("./pages/professional/Disponibilidad"));
const CitasProfesional = lazy(() => import("./pages/professional/CitasProfesional"));
const EstadisticasProfesional = lazy(() => import("./pages/professional/EstadisticasProfesional"));
const FacturacionProfesional = lazy(() => import("./pages/professional/FacturacionProfesional"));
const SolicitudesCercanas = lazy(() => import("./pages/professional/SolicitudesCercanas"));

const ReferidosProfesional = lazy(() => import("./pages/professional/ReferidosProfesional"));
const FaqProfesional = lazy(() => import("./pages/professional/FaqProfesional"));
const ComunidadProfesional = lazy(() => import("./pages/professional/ComunidadProfesional"));
const SuscripcionProfesional = lazy(() => import("./pages/professional/SuscripcionProfesional"));
const RedProfesionales = lazy(() => import("./pages/professional/RedProfesionales"));
const ConsultaTelemedicina = lazy(() => import("./pages/telemedicina/ConsultaTelemedicina"));

// Admin pages - lazy loaded
const GestionUsuarios = lazy(() => import("./pages/admin/GestionUsuarios"));
const VerificacionProfesionales = lazy(() => import("./pages/admin/VerificacionProfesionales"));
const ModeracionSolicitudes = lazy(() => import("./pages/admin/ModeracionSolicitudes"));
const ControlPagos = lazy(() => import("./pages/admin/ControlPagos"));
const GestionComunas = lazy(() => import("./pages/admin/GestionComunas"));
const GestionAreas = lazy(() => import("./pages/admin/GestionAreas"));
const ReportesAdmin = lazy(() => import("./pages/admin/ReportesAdmin"));
const ConfiguracionAdmin = lazy(() => import("./pages/admin/ConfiguracionAdmin"));
const NotificacionesAdmin = lazy(() => import("./pages/admin/NotificacionesAdmin"));
const MonitoreoTiempoReal = lazy(() => import("./pages/admin/MonitoreoTiempoReal"));
const GestionContenido = lazy(() => import("./pages/admin/GestionContenido"));
const GestionPWA = lazy(() => import("./pages/admin/GestionPWA"));

const queryClient = new QueryClient();

// Loading component
function LoadingScreen() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <div className="animate-pulse text-primary">Cargando...</div>
    </div>
  );
}

// Patient Route - Only for patients
function PatientRoute({ children }: { children: React.ReactNode }) {
  const { user, role, loading } = useAuth();
  
  if (loading) return <LoadingScreen />;
  if (!user) return <Navigate to="/auth" replace />;
  
  // Redirect non-patients to their respective dashboards
  if (role === 'admin') return <Navigate to="/admin" replace />;
  if (role && ['profesional_independiente', 'medico', 'enfermera'].includes(role)) {
    return <Navigate to="/profesional" replace />;
  }
  
  return <PatientLayout>{children}</PatientLayout>;
}

// Professional Route - Only for professionals
function ProfessionalRoute({ children }: { children: React.ReactNode }) {
  const { user, role, loading } = useAuth();
  
  if (loading) return <LoadingScreen />;
  if (!user) return <Navigate to="/auth" replace />;
  
  // Check if user is a professional
  const isProfessional = role && ['profesional_independiente', 'medico', 'enfermera'].includes(role);
  
  if (role === 'admin') return <Navigate to="/admin" replace />;
  if (!isProfessional) return <Navigate to="/dashboard" replace />;
  
  return <ProfessionalLayout>{children}</ProfessionalLayout>;
}

// Admin Route - Only for admins
function AdminRoute({ children }: { children: React.ReactNode }) {
  const { user, role, loading } = useAuth();
  
  if (loading) return <LoadingScreen />;
  if (!user) return <Navigate to="/auth" replace />;
  if (role !== 'admin') return <Navigate to="/dashboard" replace />;
  
  return <AdminLayout>{children}</AdminLayout>;
}

// Smart redirect based on role
function DashboardRedirect() {
  const { user, role, loading } = useAuth();
  
  if (loading) return <LoadingScreen />;
  if (!user) return <Navigate to="/auth" replace />;
  
  if (role === 'admin') return <Navigate to="/admin" replace />;
  if (role && ['profesional_independiente', 'medico', 'enfermera'].includes(role)) {
    return <Navigate to="/profesional" replace />;
  }
  
  // Default to patient dashboard
  return <Navigate to="/usuario" replace />;
}

function CanonicalDomainGuard() {
  useEffect(() => {
    const host = window.location.hostname;
    if (host === "www.urgsalud.com") {
      const target = `https://urgsalud.com${window.location.pathname}${window.location.search}${window.location.hash}`;
      window.location.replace(target);
    }
  }, []);

  return null;
}

function AppRoutes() {
  return (
    <Suspense fallback={<LoadingScreen />}>
      <CanonicalDomainGuard />
      <Routes>
        {/* Public routes */}
        <Route path="/" element={<Index />} />
        <Route path="/auth" element={<Auth />} />
        <Route path="/urgencia" element={<Urgencia />} />
        <Route path="/~oauth/callback" element={<OAuthCallback />} />
        <Route path="/terminos" element={<TerminosCondiciones />} />
        <Route path="/privacidad" element={<PoliticaPrivacidad />} />
        
        {/* Smart redirect from /dashboard */}
        <Route path="/dashboard" element={<DashboardRedirect />} />
        
        {/* ============ PATIENT ROUTES ============ */}
        <Route path="/usuario" element={<PatientRoute><PatientDashboard /></PatientRoute>} />
        <Route path="/buscar-profesionales" element={<PatientRoute><BuscarProfesionales /></PatientRoute>} />
        <Route path="/paciente/buscar/:especialidad" element={<PatientRoute><ResultadosBusqueda /></PatientRoute>} />
        <Route path="/buscar/:especialidad" element={<PatientRoute><ResultadosBusqueda /></PatientRoute>} />
        <Route path="/mis-citas" element={<PatientRoute><MisCitas /></PatientRoute>} />
        <Route path="/historial-medico" element={<PatientRoute><HistorialMedico /></PatientRoute>} />
        <Route path="/favoritos" element={<PatientRoute><Favoritos /></PatientRoute>} />
        <Route path="/solicitar" element={<PatientRoute><SolicitarServicio /></PatientRoute>} />
        <Route path="/solicitar/:areaId" element={<PatientRoute><SolicitarServicio /></PatientRoute>} />
        <Route path="/solicitar-servicio" element={<PatientRoute><SolicitarAtencion /></PatientRoute>} />
        <Route path="/comunidad" element={<PatientRoute><ComunidadUsuario /></PatientRoute>} />
        <Route path="/area/:areaSlug" element={<PatientRoute><ProfesionalesArea /></PatientRoute>} />
        <Route path="/profesional-perfil/:profesionalId" element={<PatientRoute><PerfilPublicoProfesional /></PatientRoute>} />
        <Route path="/agendar/:profesionalId" element={<PatientRoute><AgendarCita /></PatientRoute>} />
        
        <Route path="/referidos" element={<PatientRoute><ReferidosUsuario /></PatientRoute>} />
        <Route path="/faq" element={<PatientRoute><FaqUsuario /></PatientRoute>} />
        <Route path="/mi-cuenta" element={<PatientRoute><MiCuenta /></PatientRoute>} />
        <Route path="/ayuda" element={<PatientRoute><PatientDashboard /></PatientRoute>} />
        <Route path="/telemedicina/:sesionId" element={<PatientRoute><ConsultaTelemedicina /></PatientRoute>} />
        
        {/* ============ PROFESSIONAL ROUTES ============ */}
        <Route path="/profesional" element={<ProfessionalRoute><ProfessionalDashboard /></ProfessionalRoute>} />
        <Route path="/solicitudes" element={<ProfessionalRoute><SolicitudesCercanas /></ProfessionalRoute>} />
        <Route path="/mis-atenciones" element={<ProfessionalRoute><CitasProfesional /></ProfessionalRoute>} />
        <Route path="/disponibilidad" element={<ProfessionalRoute><Disponibilidad /></ProfessionalRoute>} />
        <Route path="/estadisticas" element={<ProfessionalRoute><EstadisticasProfesional /></ProfessionalRoute>} />
        <Route path="/facturacion" element={<ProfessionalRoute><FacturacionProfesional /></ProfessionalRoute>} />
        <Route path="/perfil" element={<ProfessionalRoute><PerfilProfesional /></ProfessionalRoute>} />
        <Route path="/comunidad-profesional" element={<ProfessionalRoute><ComunidadProfesional /></ProfessionalRoute>} />
        <Route path="/red-profesionales" element={<ProfessionalRoute><RedProfesionales /></ProfessionalRoute>} />
        
        <Route path="/referidos-profesional" element={<ProfessionalRoute><ReferidosProfesional /></ProfessionalRoute>} />
        <Route path="/suscripcion" element={<ProfessionalRoute><SuscripcionProfesional /></ProfessionalRoute>} />
        <Route path="/faq-profesional" element={<ProfessionalRoute><FaqProfesional /></ProfessionalRoute>} />
        <Route path="/profesional/telemedicina/:sesionId" element={<ProfessionalRoute><ConsultaTelemedicina /></ProfessionalRoute>} />
        
        {/* ============ ADMIN ROUTES ============ */}
        <Route path="/admin" element={<AdminRoute><AdminDashboard /></AdminRoute>} />
        <Route path="/admin/usuarios" element={<AdminRoute><GestionUsuarios /></AdminRoute>} />
        <Route path="/admin/verificacion" element={<AdminRoute><VerificacionProfesionales /></AdminRoute>} />
        <Route path="/admin/solicitudes" element={<AdminRoute><ModeracionSolicitudes /></AdminRoute>} />
        <Route path="/admin/pagos" element={<AdminRoute><ControlPagos /></AdminRoute>} />
        <Route path="/admin/comunas" element={<AdminRoute><GestionComunas /></AdminRoute>} />
        <Route path="/admin/areas" element={<AdminRoute><GestionAreas /></AdminRoute>} />
        <Route path="/admin/reportes" element={<AdminRoute><ReportesAdmin /></AdminRoute>} />
        <Route path="/admin/configuracion" element={<AdminRoute><ConfiguracionAdmin /></AdminRoute>} />
        <Route path="/admin/notificaciones" element={<AdminRoute><NotificacionesAdmin /></AdminRoute>} />
        <Route path="/admin/monitoreo" element={<AdminRoute><MonitoreoTiempoReal /></AdminRoute>} />
        <Route path="/admin/contenido" element={<AdminRoute><GestionContenido /></AdminRoute>} />
        <Route path="/admin/pwa" element={<AdminRoute><GestionPWA /></AdminRoute>} />
        
        {/* 404 */}
        <Route path="*" element={<NotFound />} />
      </Routes>
    </Suspense>
  );
}

function FloatingAssistant() {
  const { user } = useAuth();
  if (!user) return null;
  return <UrgSaludAssistant />;
}

const App = () => (
  <QueryClientProvider client={queryClient}>
    <ThemeProvider>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <BrowserRouter>
          <AuthProvider>
            <PushNotificationInit />
            <AppRoutes />
            <FloatingAssistant />
          </AuthProvider>
        </BrowserRouter>
      </TooltipProvider>
    </ThemeProvider>
  </QueryClientProvider>
);

export default App;
